import React, { lazy, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
  Typography,
  Box,
  ImageList,
  ImageListItem,
  IconButton,
  ImageListItemBar,
  Card,
  CardActionArea,
  CardMedia,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { COLOR } from 'config/constants';
import { useListing } from 'hooks';
import RadioButtonUncheckedOutlinedIcon from '@material-ui/icons/RadioButtonUncheckedOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { GridContextProvider, GridDropZone, GridItem, swap, move } from 'react-grid-dnd';
import { images } from 'assets/images';
import { Picture, VideoTemplate } from 'models';

const grid_row_items_count = 4;

const useStyles = makeStyles({
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  iconText: {
    display: 'flex',
    flexDirection: 'row',
  },
  imageContent: {
    display: 'flex',
  },
  imageListItem: {
    width: '100%',
  },
  icon: {
    color: COLOR.LIGHT_BLUE,
    flexDirection: 'column',
    display: 'flex',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginBottom: 5,
  },
  gridContainer: {
    touchAction: 'none',
    width: '100%',
    height: '100%',
    marginBottom: 10,
  },
  selectedPicture: {
    height: 80,
  },
  gridItem: {
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    cursor: 'move',
    padding: 5,
  },
  info: {
    color: COLOR.DARK_GREY,
    fontSize: 11,
    marginLeft: 5,
  },
  selected: {
    fontWeight: 'bold',
    fontSize: 14,
  },
});
interface Props {
  selectedPictures: Picture[];
  onSelectPicture: (_: Picture) => void;
  picturesWithNewOrder: Picture[];
  setPicturesWithNewOrder: (_: any) => void;
  selectedTemplate: VideoTemplate;
}
function PictureSelection({
  selectedPictures,
  onSelectPicture,
  picturesWithNewOrder,
  setPicturesWithNewOrder,
  selectedTemplate,
}: Props) {
  const intl = useIntl();
  const styles = useStyles();
  const path = window.location.pathname;
  const arr = path.split('/');
  const listing_token = arr[arr.length - 1];
  const { listing } = useListing(listing_token);
  const filteredPictures = listing.pictures.filter((picture) => {
    return picture.distrib_url !== null;
  });
  const [rowsOfCards, setRowOfCards] = useState(0);
  const fmtDragPhotos = intl.formatMessage({ id: 'deliver.video_slideshow.dragPhotos' });
  const fmtSelected = intl.formatMessage({ id: 'deliver.video_slideshow.selected' });

  const handleResize = () => {
    let rows = Math.ceil(picturesWithNewOrder.length / grid_row_items_count);
    if (rows === 0) {
      rows = 1;
    }
    setRowOfCards(rows);
  };
  useEffect(() => {
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [picturesWithNewOrder]);
  const onChangeGrid = (sourceId, sourceIndex, targetIndex, targetId) => {
    const newPictures = swap(picturesWithNewOrder, sourceIndex, targetIndex);
    setPicturesWithNewOrder(newPictures);
  };
  return (
    <Box>
      {selectedPictures.length ? (
        <Box>
          <Box className={styles.row}>
            <Box>
              <Typography className={styles.selected}>
                {`${selectedPictures.length} / ${selectedTemplate.listing_picture_count} ${fmtSelected}`}
              </Typography>
            </Box>
            <Box className={styles.iconText}>
              <img style={{ height: 20, width: 20, marginLeft: 10 }} src={images.info} alt='info' />
              <Typography className={styles.info}>{fmtDragPhotos}</Typography>
            </Box>
          </Box>
          <GridContextProvider onChange={onChangeGrid}>
            <Box className={styles.gridContainer}>
              <GridDropZone
                className='dropzone'
                boxesPerRow={grid_row_items_count}
                rowHeight={90}
                style={{ height: rowsOfCards * 90 + 10 }}
                id='pictureItems'
              >
                {picturesWithNewOrder.map((p, index) => {
                  return (
                    <GridItem key={p.id}>
                      <Box className={styles.gridItem}>
                        <Card variant='outlined'>
                          <CardActionArea>
                            <CardMedia
                              className={styles.selectedPicture}
                              image={p.thumbnail_url}
                              title='thumbnail'
                            />
                          </CardActionArea>
                        </Card>
                      </Box>
                    </GridItem>
                  );
                })}
              </GridDropZone>
            </Box>
          </GridContextProvider>
        </Box>
      ) : null}
      <Box>
        <ImageList gap={12}>
          {filteredPictures.length &&
            filteredPictures.map((picture: Picture, index) => {
              const isSelected = selectedPictures.includes(picture);
              return (
                <ImageListItem key={index} className={styles.imageContent}>
                  <img
                    className={styles.imageListItem}
                    src={picture.thumbnail_url}
                    alt='thumbnail'
                  />
                  <ImageListItemBar
                    actionIcon={
                      <Box className={styles.column}>
                        <IconButton
                          onClick={() => {
                            return onSelectPicture(picture);
                          }}
                          className={styles.icon}
                        >
                          {isSelected ? <CheckCircleIcon /> : <RadioButtonUncheckedOutlinedIcon />}
                        </IconButton>
                      </Box>
                    }
                  />
                </ImageListItem>
              );
            })}
        </ImageList>
      </Box>
    </Box>
  );
}
export default PictureSelection;
